import { Action, createReducer, on } from '@ngrx/store';
import * as actions from './disney-tickets.actions';
import { IDisneyClusterTickets } from '@core/models/modals';
import { Rate } from '@core/models/Rate/Rate.model';
import { Supplements } from '@core/models/Supplement/Supplements.model';

export interface IStateDisneyTickets {
  disneyTicketsCluster: IDisneyClusterTickets;
  ticketsMap: Map<number, Supplements[]>;
  ticketSelected: Supplements;
  ratesByTicket: Rate[];
  rateSelected: Rate;
  defaultRate: Rate;
  startDate: Date;
  rateCheckInProgress: boolean;
  checkRateFail: boolean;
}

const initialState: IStateDisneyTickets = {
  disneyTicketsCluster: null,
  ticketsMap: new Map(),
  ticketSelected: null,
  ratesByTicket: [],
  rateSelected: null,
  defaultRate: null,
  startDate: null,
  rateCheckInProgress: false,
  checkRateFail: false,
};

const _disneyTicketsReducer = createReducer(
  initialState,
  on(actions.disneyTickets, (state, { disneyTicketCluster }) => {
    const supplementsMap: Map<number, Supplements[]> = new Map();
    disneyTicketCluster.cluster.rates.forEach((rate: Rate) => {
      if (rate.supplements) {
        rate.supplements.forEach((supplement) => {
          (supplement as any).rate_id = rate.id;
          if (supplement.type === 'TICKET') {
            if (supplement.ticket === '') {
              if (supplement.description.includes('Base Ticket')) {
                supplement.ticket = 'BASICO';
              }
              if (supplement.description.includes('Sports Option')) {
                supplement.ticket = 'BASICO_PLUS';
              }
            }

            if (supplementsMap.has(supplement.days)) {
              supplementsMap.get(supplement.days).push(supplement);
            } else {
              supplementsMap.set(supplement.days, [supplement]);
            }
          }
        });
      } else {
        rate.supplements = [];
      }
    });
    return {
      ...state,
      disneyTicketsCluster: disneyTicketCluster,
      ticketsMap: supplementsMap,
      defaultRate: disneyTicketCluster.preSelectedRate,
    };
  }),
  on(actions.setSelectedTicket, (state, { ticket }) => {
    const rates = state.disneyTicketsCluster.cluster.rates.filter((rate: Rate) => {
      if (rate.supplements) {
        const tickets = rate.supplements.filter(
          (supplement: Supplements) =>
            supplement.days === ticket.days && supplement.ticket === ticket.ticket,
        );
        return tickets.length === 1;
      }
      return false;
    });
    return {
      ...state,
      ticketSelected: ticket,
      ratesByTicket: rates,
    };
  }),
  on(actions.unsetSelectedTicket, (state) => {
    return {
      ...state,
      ticketSelected: null,
      ratesByTicket: [],
      rateSelected: state.defaultRate,
    };
  }),
  on(actions.setSelectedRate, (state, { rate }) => {
    return {
      ...state,
      rateSelected: rate,
      rateCheckInProgress: false,
    };
  }),
  on(actions.checkSelectedRate, (state) => {
    return {
      ...state,
      rateCheckInProgress: true,
    };
  }),
  on(actions.unsetSelectedRate, (state) => {
    return {
      ...state,
      rateSelected: null,
    };
  }),
  on(actions.resetSelectedRate, (state) => {
    return {
      ...state,
      rateSelected: state.defaultRate,
    };
  }),
  on(actions.resetDefaultRate, (state) => {
    return {
      ...state,
      defaultRate: (state.disneyTicketsCluster.preSelectedRate as unknown) as Rate,
    };
  }),
  on(actions.setDefaultRate, (state, { rate }) => {
    return {
      ...state,
      defaultRate: rate,
    };
  }),
  on(actions.setStartDate, (state, { selectedDate }) => {
    return {
      ...state,
      startDate: selectedDate,
    };
  }),
  on(actions.resetDisneyTickets, (state) => {
    return { ...initialState };
  }),
  on(actions.checkRateFailed, (state) => {
    return {
      ...state,
      checkRateFail: true,
    };
  }),
  on(actions.checkRateSuccessful, (state) => {
    return {
      ...state,
      checkRateFail: false,
    };
  }),
);

export function disneyTicketsReducer(state: IStateDisneyTickets, action: Action) {
  return _disneyTicketsReducer(state, action);
}
